import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { ETheme, type ThemeColors } from './types';

import { applyTheme } from '../lib/applyTheme';
import { themeTokens } from '../lib/tokens';

const THEME_STORAGE_KEY = 'mm-theme';

interface ThemeState {
  theme: ETheme;
  colors: ThemeColors;
  isDark: boolean;
  setTheme: (theme: ETheme) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: ETheme.dark,
      colors: themeTokens[ETheme.dark],
      isDark: true,

      setTheme: (theme: ETheme) => {
        const colors = themeTokens[theme];
        applyTheme(colors);
        set({ theme, colors, isDark: theme === ETheme.dark });
      },

      toggleTheme: () => {
        const newTheme = get().theme === ETheme.dark ? ETheme.light : ETheme.dark;
        get().setTheme(newTheme);
      },
    }),
    {
      name: THEME_STORAGE_KEY,
      partialize: state => ({ theme: state.theme }),
      onRehydrateStorage: () => state => {
        if (state) {
          const colors = themeTokens[state.theme];
          state.colors = colors;
          state.isDark = state.theme === ETheme.dark;
          applyTheme(colors);
        }
      },
    },
  ),
);

// Optimized selectors to prevent unnecessary re-renders
export const useTheme = () => useThemeStore(s => s.theme);
export const useIsDark = () => useThemeStore(s => s.isDark);
export const useThemeColors = () => useThemeStore(s => s.colors);
export const useSetTheme = () => useThemeStore(s => s.setTheme);
export const useToggleTheme = () => useThemeStore(s => s.toggleTheme);
